import { createContext, createElement, ReactNode, useContext, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { UserType } from '../../interfaces/userType.enum';
import { usePermission } from '../../hooks/usePermission';
import { PERMISSIONS } from '../../constants/permissions';
import { InstitutionService } from '../../services/institution.service';
import { defaultPresetForRoles, PresetKey } from './registry/presets';
import { GATED_MODULE_KEYS } from './registry/moduleMeta';
import { ModuleKey, widgetsForRole } from './registry';

export interface DashboardAccess {
    /** Board presets the user may see, in display order. Never empty. */
    available: PresetKey[];
    /** The user's own (editable/persisted) board — role default when granted, else the first granted. */
    primary: PresetKey;
    /** Modules the user's roles hold at least one registry widget in, AND enabled for the institution. */
    visibleModules: Set<ModuleKey>;
    /** Institution's enabled module set (fail-open to all 8 on fetch error/absence). */
    enabledModules: Set<ModuleKey>;
    /** Role-eligible modules the institution has NOT enabled — drives the upsell surface. */
    upsellModules: Set<ModuleKey>;
    /** Whether the institution has the premium (business-owner) dashboard unlocked. */
    premiumEnabled: boolean;
    /** True when the user could hold the owner board but the institution has not unlocked premium. */
    showUpgradeHint: boolean;
    /** False while an owner-capable user's premium check is still in flight. Guards that would
     *  redirect on "board not granted" must wait for this — on first render `available` collapses
     *  to the basic preset until the fetch settles, which is a pending state, not an answer. */
    ready: boolean;
}

/**
 * Single source of truth for which dashboard board(s) and module deep-pages a user may see.
 *
 * The business-owner board is a premium feature: it appears ONLY when the institution has premium
 * unlocked (flipped on by a SaaS Operator) AND the user is explicitly granted DASHBOARD_VIEW_OWNER.
 * It is never a role default. The Manager and Accountant boards are free and follow the usual grant/
 * role-fallback logic. When nothing else is granted, the user falls back to the free `basic` board.
 *
 * Shared by DashboardBoard, ModuleBoard, and the sidebar submenu so all three stay consistent.
 */
/**
 * @param inert when true a provider above is already supplying the answer, so this copy skips both
 *              network calls. The hook still runs (rules of hooks) but its result is discarded.
 */
function useComputedDashboardAccess(inert = false): DashboardAccess {
    const { user, selectedInstitutionId } = useAuth();
    const roles = useMemo<UserType[]>(() => {
        const r = user?.roles;
        return (Array.isArray(r) ? r : r ? [r] : []) as UserType[];
    }, [user]);

    // The owner board is only ever available by explicit grant (never a role fallback), so its
    // fallback is hard-false. Manager/Accountant keep their role fallbacks (those boards are free).
    const ownerGranted = usePermission(PERMISSIONS.DASHBOARD_VIEW_OWNER, false);
    const canManager = usePermission(PERMISSIONS.DASHBOARD_VIEW_MANAGER, roles.includes(UserType.InstitutionManager));
    const canAccountant = usePermission(PERMISSIONS.DASHBOARD_VIEW_ACCOUNTANT, roles.includes(UserType.Accountant));

    // Fetch the institution's premium entitlement for anyone who could hold the owner board: the
    // InstitutionAdmin (owner-capable by role) or a user explicitly granted the owner view. The GET
    // is tenancy-guarded server-side; other roles never call it (and never need it — their boards are free).
    const ownerCapable = roles.includes(UserType.InstitutionAdmin) || ownerGranted;
    const [premiumEnabled, setPremiumEnabled] = useState(false);
    const [premiumResolved, setPremiumResolved] = useState(false);
    useEffect(() => {
        if (inert) return;
        if (!selectedInstitutionId || !ownerCapable) {
            setPremiumEnabled(false);
            setPremiumResolved(true);
            return;
        }
        let alive = true;
        setPremiumResolved(false);
        InstitutionService.getPremiumDashboard(selectedInstitutionId)
            .then(v => { if (alive) setPremiumEnabled(v); })
            .catch(() => { if (alive) setPremiumEnabled(false); })
            .finally(() => { if (alive) setPremiumResolved(true); });
        return () => { alive = false; };
    }, [selectedInstitutionId, ownerCapable, inert]);

    // Owner board only when the institution has unlocked premium AND the user is granted it.
    const canOwner = premiumEnabled && ownerGranted;

    // Institution's enabled-modules set. Fail-open to the full gated set on error/absence so a
    // transient network error never hides a paid module (gating is a product seam, not an authz
    // boundary — the authz boundary remains role @PreAuthorize + tenancy, server-side).
    const [enabledModules, setEnabledModules] = useState<Set<ModuleKey>>(() => new Set(GATED_MODULE_KEYS));
    const [enabledResolved, setEnabledResolved] = useState(false);
    useEffect(() => {
        if (inert) return;
        if (!selectedInstitutionId) {
            setEnabledModules(new Set(GATED_MODULE_KEYS));
            setEnabledResolved(true);
            return;
        }
        let alive = true;
        setEnabledResolved(false);
        try {
            // try/catch guards a synchronously-throwing/absent getEnabledModules (e.g. an older test
            // double that hasn't been given this method) so it fails open exactly like a rejected
            // fetch, rather than escaping as an uncaught render-time exception.
            InstitutionService.getEnabledModules(selectedInstitutionId)
                .then(mods => {
                    if (!alive) return;
                    const valid = mods.filter((m): m is ModuleKey => (GATED_MODULE_KEYS as string[]).includes(m));
                    setEnabledModules(new Set(valid));
                })
                .catch(() => { if (alive) setEnabledModules(new Set(GATED_MODULE_KEYS)); })
                .finally(() => { if (alive) setEnabledResolved(true); });
        } catch {
            setEnabledModules(new Set(GATED_MODULE_KEYS));
            setEnabledResolved(true);
        }
        return () => { alive = false; };
    }, [selectedInstitutionId, inert]);

    const roleDefault = defaultPresetForRoles(roles);

    const available = useMemo<PresetKey[]>(() => {
        const grants: Array<[PresetKey, boolean]> = [['owner', canOwner], ['manager', canManager], ['accountant', canAccountant]];
        const list = grants.filter(([, ok]) => ok).map(([p]) => p);
        return list.length > 0 ? list : [roleDefault];
    }, [canOwner, canManager, canAccountant, roleDefault]);

    const primary = available.includes(roleDefault) ? roleDefault : available[0];

    // Modules the user's roles can reach at least one widget in — registry-driven, NOT preset-
    // driven. Deriving this from ROLE_PRESETS meant a module could never become visible (or
    // upsellable) until it appeared on a home-board preset; the business modules ship deep boards
    // without ever joining a home preset, so the union runs over the whole registry instead.
    // visibleModules/upsellModules below still intersect/subtract the institution's enabled set.
    const roleModules = useMemo<Set<ModuleKey>>(() => {
        const mods = new Set<ModuleKey>();
        widgetsForRole(roles).forEach(w => mods.add(w.module));
        return mods;
    }, [roles]);

    const visibleModules = useMemo<Set<ModuleKey>>(
        () => new Set(Array.from(roleModules).filter(m => enabledModules.has(m))),
        [roleModules, enabledModules],
    );

    const upsellModules = useMemo<Set<ModuleKey>>(
        () => new Set(Array.from(roleModules).filter(m => !enabledModules.has(m))),
        [roleModules, enabledModules],
    );

    // A non-premium InstitutionAdmin is looking at the basic board and can be nudged to upgrade to the
    // owner dashboard. Once premium is unlocked the hint disappears (the owner board becomes available).
    const showUpgradeHint = roles.includes(UserType.InstitutionAdmin) && !premiumEnabled;

    return { available, primary, visibleModules, enabledModules, upsellModules, premiumEnabled, showUpgradeHint, ready: premiumResolved && enabledResolved };
}

const DashboardAccessContext = createContext<DashboardAccess | null>(null);

/**
 * Computes dashboard access ONCE for everything below it.
 *
 * Without it each consumer runs its own copy of the effects above, so the always-mounted Sidebar
 * and whichever board is showing each issue their own `getPremiumDashboard` + `getEnabledModules`,
 * and every navigation between boards remounts the board consumer and refetches both. Mounted at
 * the authenticated shell in App.tsx, the pair is fetched once per institution instead.
 *
 * The hook deliberately still works WITHOUT this provider: `useDashboardAccess` falls back to
 * computing locally, so a component (or a test) rendered on its own behaves exactly as before.
 */
export function DashboardAccessProvider({ children }: { children: ReactNode }) {
    const value = useComputedDashboardAccess();
    return createElement(DashboardAccessContext.Provider, { value }, children);
}

export function useDashboardAccess(): DashboardAccess {
    const shared = useContext(DashboardAccessContext);
    // Hooks must run unconditionally, so the local computation always runs; when a provider is
    // present its value wins and the local copy is inert. The provider is what removes the
    // duplicate NETWORK calls — see below.
    const local = useComputedDashboardAccess(!!shared);
    return shared ?? local;
}
